import React, { useState } from 'react';
import { FaGraduationCap, FaBriefcase, FaStar, FaDownload } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface Experience {
  key:
    | 'afterquery' | 'aobabbt' | 'revelo' | 'betaTech' | 'etech' | 'dataAnnotation'
    | 'upwork' | 'quoteVote' | 'menas' | 'zerone' | 'csecLead' | 'ctf';
  link?: { url: string };
}

interface Education {
  key: 'astu';
}

type RoleType = 'onsite' | 'remote' | 'freelance' | 'opensource' | 'intern' | 'community' | 'competition';

const ROLE_TYPE_LABEL: Record<RoleType, string> = {
  onsite: 'ONSITE',
  remote: 'REMOTE',
  freelance: 'FREELANCE',
  opensource: 'OPEN SOURCE',
  intern: 'INTERN',
  community: 'COMMUNITY',
  competition: 'COMPETITION',
};

// Same order as the CV: current roles first, then reverse-chronological
const experiences: (Experience & { roleType: RoleType })[] = [
  { key: 'afterquery',     roleType: 'remote' },
  { key: 'revelo',         roleType: 'remote' },
  { key: 'upwork',         roleType: 'freelance',  link: { url: 'https://www.upwork.com/freelancers/~motirebuma' } },
  { key: 'aobabbt',        roleType: 'onsite' },
  { key: 'betaTech',       roleType: 'remote' },
  { key: 'dataAnnotation', roleType: 'remote' },
  { key: 'etech',          roleType: 'remote' },
  { key: 'menas',          roleType: 'intern' },
  { key: 'zerone',         roleType: 'intern' },
  { key: 'quoteVote',      roleType: 'opensource', link: { url: 'https://quote.vote' } },
  { key: 'csecLead',       roleType: 'community' },
  { key: 'ctf',            roleType: 'competition', link: { url: 'https://cybertalents.com' } }
];

const education: Education[] = [
  {
    key: 'astu'
  }
];

const DESC_LIMIT = 160;

const truncate = (text: string, max = DESC_LIMIT) => {
  if (text.length <= max) return text;
  const cut = text.lastIndexOf(' ', max);
  return text.slice(0, cut > 0 ? cut : max).trimEnd() + '…';
};

const Resume: React.FC = () => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { theme } = useTheme();
  const { t } = useTranslation();
  const highlights = (() => {
    const list = t('about.card.uniqueList', { returnObjects: true });
    return Array.isArray(list) ? (list as string[]) : [];
  })();
  const toggleExpand = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const getSectionStyles = () => {
    if (theme === 'modern') {
      return 'modern-section py-12 sm:py-16 lg:py-20 min-h-screen relative overflow-x-clip';
    }
    return 'py-12 sm:py-16 lg:py-20 min-h-screen relative overflow-x-clip';
  };

  const getContainerStyles = () => {
    if (theme === 'modern') {
      return 'max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 flex flex-col items-center justify-center relative z-10';
    }
    return 'max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 flex flex-col items-center justify-center relative z-10';
  };

  const getTitleStyles = () => {
    if (theme === 'modern') {
      return 'text-2xl xs:text-3xl sm:text-4xl lg:text-5xl font-bold modern-heading mb-4 sm:mb-6 bg-gradient-to-r from-gray-900 via-blue-900 to-purple-900 bg-clip-text text-transparent text-center relative z-10';
    }
    return 'text-2xl xs:text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-black mb-4 sm:mb-6 text-center relative inline-block z-10';
  };

  const getSubtitleStyles = () => {
    if (theme === 'modern') {
      return 'text-sm sm:text-base lg:text-xl modern-subheading mb-10 sm:mb-12 lg:mb-16 opacity-80 text-center max-w-3xl mx-auto px-4';
    }
    return 'text-sm sm:text-base lg:text-xl text-gray-800 mb-10 sm:mb-12 lg:mb-16 font-bold text-center max-w-3xl mx-auto px-4';
  };

  const getCardStyles = () => {
    if (theme === 'modern') {
      return 'modern-card p-4 sm:p-6 lg:p-8 xl:p-10 rounded-2xl sm:rounded-3xl shadow-xl';
    }
    return 'bg-white border-4 sm:border-6 border-black rounded-lg p-4 sm:p-6 lg:p-8 xl:p-10 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] sm:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] transform hover:-translate-y-2 hover:-translate-x-2 hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[14px_14px_0px_0px_rgba(0,0,0,1)] transition-all duration-300';
  };

  const getSectionTitleStyles = () => {
    if (theme === 'modern') {
      return 'text-xl sm:text-2xl lg:text-3xl font-bold modern-heading flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8';
    }
    return 'text-xl sm:text-2xl lg:text-3xl font-black flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8 text-black';
  };

  const getItemCardStyles = (index: number, isEducation: boolean = false) => {
    const baseStyles = theme === 'modern' 
      ? 'modern-card p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl'
      : 'bg-white border-2 sm:border-4 border-black rounded-lg p-4 sm:p-6 lg:p-8 transition-all duration-300 hover:-translate-y-3 hover:-translate-x-3 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] hover:rotate-1 transform-gpu';
    
    const hoverIndex = isEducation ? index + experiences.length : index;
    
    if (theme === 'modern') {
      return `${baseStyles} ${hoveredIndex === hoverIndex ? 'ring-2 ring-blue-500 shadow-2xl' : ''}`;
    }
    
    return baseStyles;
  };

  const getDotColor = (index: number) => {
    if (theme === 'modern') {
      if (hoveredIndex === null) return 'bg-emerald-200';
      if (hoveredIndex === index) return 'bg-emerald-500 scale-125';
      if (hoveredIndex > index) return 'bg-emerald-300';
      return 'bg-emerald-200';
    }
    
    if (hoveredIndex === null) return 'bg-black';
    if (hoveredIndex === index) return 'bg-purple-400 scale-125'; // Changed from yellow to purple
    if (hoveredIndex > index) return 'bg-gray-400';
    return 'bg-black';
  };

  const getLineColor = (index: number) => {
    if (theme === 'modern') {
      if (hoveredIndex === null) return 'bg-emerald-100';
      if (hoveredIndex >= index) return 'bg-emerald-500';
      return 'bg-emerald-100';
    }
    
    if (hoveredIndex === null) return 'bg-black';
    if (hoveredIndex >= index) return 'bg-purple-400'; // Changed from yellow to purple
    return 'bg-black';
  };

  const getItemTitleStyles = () => {
    if (theme === 'modern') {
      return 'text-lg sm:text-xl lg:text-2xl font-bold modern-heading mb-2';
    }
    return 'text-lg sm:text-xl lg:text-2xl font-black mb-2 text-black';
  };

  const getItemCompanyStyles = () => {
    if (theme === 'modern') {
      return 'text-sm sm:text-base lg:text-lg modern-subheading mb-2';
    }
    return 'text-sm sm:text-base lg:text-lg text-gray-600 mb-2 font-bold';
  };

  const getItemPeriodStyles = () => {
    if (theme === 'modern') {
      return 'text-xs sm:text-sm accent-gray mb-3 sm:mb-4 font-medium';
    }
    return 'text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 font-bold';
  };

  const getItemDescriptionStyles = () => {
    if (theme === 'modern') {
      return 'modern-text leading-relaxed text-sm sm:text-base';
    }
    return 'text-gray-700 leading-relaxed text-sm sm:text-base font-medium';
  };

  const getLinkStyles = () => {
    if (theme === 'modern') {
      return 'accent-blue hover:underline mt-3 sm:mt-4 inline-block transition-colors font-medium text-sm sm:text-base';
    }
    return 'text-blue-600 hover:underline mt-3 sm:mt-4 inline-block font-bold hover:text-purple-600 transition-colors text-sm sm:text-base';
  };

  const getIconStyles = () => {
    if (theme === 'modern') {
      return 'text-2xl sm:text-3xl accent-primary';
    }
    return 'text-3xl sm:text-4xl text-purple-600';
  };

  return (
    <section id="resume" className={getSectionStyles()}>
      {/* Small Decorative Elements - Neobrutalism Theme Only */}
      {theme === 'modern' && (
        <div className="absolute inset-0 -z-10">
          {/* Very small animated blobs - hidden on mobile for performance */}
          <div className="hidden sm:block absolute top-20 right-20 w-16 h-16 bg-emerald-200 rounded-full mix-blend-multiply filter blur-sm opacity-40 animate-blob"></div>
          <div className="hidden sm:block absolute bottom-40 left-20 w-20 h-20 bg-teal-200 rounded-full mix-blend-multiply filter blur-sm opacity-35 animate-blob animation-delay-2000"></div>
          <div className="hidden md:block absolute top-1/2 left-10 w-12 h-12 bg-emerald-300 rounded-full mix-blend-multiply filter blur-sm opacity-30 animate-blob animation-delay-4000"></div>
          <div className="hidden md:block absolute bottom-20 right-1/3 w-18 h-18 bg-teal-300 rounded-full mix-blend-multiply filter blur-sm opacity-28 animate-blob"></div>
          <div className="hidden lg:block absolute top-10 left-1/3 w-14 h-14 bg-emerald-100 rounded-full mix-blend-multiply filter blur-sm opacity-25 animate-blob animation-delay-3000"></div>
          <div className="hidden lg:block absolute bottom-10 left-1/2 w-10 h-10 bg-teal-100 rounded-full mix-blend-multiply filter blur-sm opacity-20 animate-blob animation-delay-5000"></div>

          {/* Additional tiny floating elements - desktop only */}
          <div className="hidden xl:block absolute top-1/4 right-1/4 w-8 h-8 bg-emerald-200 rounded-full mix-blend-multiply filter blur-sm opacity-18 animate-float"></div>
          <div className="hidden xl:block absolute bottom-1/4 left-1/4 w-8 h-8 bg-teal-200 rounded-full mix-blend-multiply filter blur-sm opacity-15 animate-float animation-delay-2000"></div>
        </div>
      )}

      <div className={getContainerStyles()}>
        {/* Hero Section */}
        <div className="text-center relative w-full mt-16 sm:mt-20">
          {theme === 'neobrutalism' && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 sm:w-56 lg:w-72 h-48 sm:h-56 lg:h-72 bg-yellow-400/25 rounded-full filter blur-2xl animate-pulse -z-10"></div>
          )}

          {/* Creative header strip per theme */}
          {theme === 'modern' ? (
            <div className="sf-resume-strip">
              <span className="sf-resume-strip__tag">MISSION_LOG</span>
              <span className="sf-dossier__sep">▌</span>
              <span className="sf-resume-strip__id">{String(experiences.length).padStart(2, '0')}_OPS_LOGGED</span>
              <span className="sf-dossier__sep">▌</span>
              <span className="sf-resume-strip__live">
                <span className="sf-dossier__livedot"></span>
                ARCHIVED
              </span>
            </div>
          ) : (
            <div className="nb-resume-strip">
              <span className="nb-resume-strip__star">★</span>
              <span>OPS &amp; CV</span>
              <span className="nb-resume-strip__pipe">|</span>
              <span>{experiences.length} ENTRIES</span>
              <span className="nb-resume-strip__pipe">|</span>
              <span className="nb-resume-strip__star">★</span>
            </div>
          )}

          <div className="relative group">
            <h1 className={getTitleStyles()}>
              {t('resume.title')}
              {theme === 'neobrutalism' && <div className="absolute -bottom-2 sm:-bottom-3 left-1/2 transform -translate-x-1/2 w-3/4 h-4 sm:h-6 bg-yellow-400 -z-10 rotate-2"></div>}
            </h1>
          </div>

          <p className={getSubtitleStyles()}>
            {theme === 'modern' ? (
              <>
                <span className="sf-bracket">[</span>
                <span>{t('resume.subtitle')}</span>
                <span className="sf-bracket">]</span>
              </>
            ) : (
              t('resume.subtitle')
            )}
          </p>

          {/* Counter band — totals */}
          {theme === 'modern' ? (
            <div className="sf-resume-counter">
              <span>OPERATIONS <strong>{String(experiences.length).padStart(2, '0')}</strong></span>
              <span className="sf-modules__counter-bar"></span>
              <span>RUNTIME <strong>06Y+</strong></span>
              <span className="sf-modules__counter-bar"></span>
              <span>EDUCATION <strong>{String(education.length).padStart(2, '0')}</strong></span>
              <span className="sf-modules__counter-bar"></span>
              <span className="sf-modules__counter-status">● ACTIVE</span>
            </div>
          ) : null}
        </div>

        {/* Enhanced Resume Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 py-6 sm:py-8 lg:py-10 w-full relative">
          {/* Resume container with subtle glow effect - Neobrutalism Only */}
          <div className="relative group col-span-full">
            {theme === 'neobrutalism' && (
              <div className="hidden sm:block absolute -inset-2 bg-gradient-to-r from-yellow-200/15 via-purple-200/20 to-pink-200/15 rounded-lg blur-md opacity-0 group-hover:opacity-100 transition duration-1000 -z-10"></div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)] gap-8 sm:gap-10 lg:gap-12 items-start w-full">
              {/* Experience Section */}
              <div className={getCardStyles()}>
                <div className={getSectionTitleStyles()}>
                  <FaBriefcase className={getIconStyles()} />
                  <h3>{t('resume.experience.title')}</h3>
                </div>
                <div className="relative">
                  {experiences.map((exp, index) => {
                    const linkText = exp.link ? t(`resume.experience.items.${exp.key}.linkText`, { defaultValue: '' }) : '';
                    return (
                      <div 
                        key={index} 
                        className="relative ps-8 sm:ps-10 lg:ps-12 mb-8 sm:mb-10 lg:mb-12 last:mb-0"
                        onMouseEnter={() => setHoveredIndex(index)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                      {/* Timeline dot */}
                      <div 
                        className={`absolute start-0 top-2 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full transition-all duration-300 ${getDotColor(index)}`}
                      ></div>
                      {/* Connecting line */}
                      {index < experiences.length - 1 && (
                        <div 
                          className={`absolute start-[7px] sm:start-[9px] lg:start-[11px] top-6 sm:top-8 lg:top-9 w-0.5 sm:w-1 h-[calc(100%+1rem)] sm:h-[calc(100%+1.5rem)] lg:h-[calc(100%+2rem)] transition-colors duration-300 ${getLineColor(index)}`}
                        ></div>
                      )}
                      <div className={getItemCardStyles(index)}>
                        {theme === 'modern' ? (
                          <>
                            <div className="sf-log-tag">
                              <span className="sf-log-tag__code">LOG_{String(index + 1).padStart(3, '0')}</span>
                              <span className="sf-log-tag__bar"></span>
                              <span className="sf-log-tag__period">{t(`resume.experience.items.${exp.key}.period`)}</span>
                            </div>
                            <span className={`sf-role-chip sf-role-chip--${exp.roleType}`}>
                              <span className="sf-role-chip__dot"></span>
                              {ROLE_TYPE_LABEL[exp.roleType]}
                            </span>
                          </>
                        ) : (
                          <>
                            <div className="nb-log-tag">
                              <span className="nb-log-tag__code">LOG_{String(index + 1).padStart(3, '0')}</span>
                              <span className="nb-log-tag__period">{t(`resume.experience.items.${exp.key}.period`)}</span>
                            </div>
                            <span className={`nb-role-chip nb-role-chip--${exp.roleType}`}>
                              ★ {ROLE_TYPE_LABEL[exp.roleType]}
                            </span>
                          </>
                        )}
                        <h4 className={getItemTitleStyles()}>{t(`resume.experience.items.${exp.key}.title`)}</h4>
                        <p className={getItemCompanyStyles()}>{t(`resume.experience.items.${exp.key}.company`)}</p>
                        {(() => {
                          const desc = t(`resume.experience.items.${exp.key}.description`);
                          const itemKey = `exp-${index}`;
                          const isExpanded = !!expanded[itemKey];
                          const needsToggle = desc.length > DESC_LIMIT;
                          return (
                            <>
                              <p className={getItemDescriptionStyles()}>
                                {needsToggle && !isExpanded ? truncate(desc) : desc}
                              </p>
                              {needsToggle && (
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(itemKey)}
                                  className={theme === 'modern' ? 'sf-readmore' : 'nb-readmore'}
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? (t('resume.readLess', { defaultValue: 'Read less' })) : (t('resume.readMore', { defaultValue: 'Read more' }))}
                                  <span className="sr-only">{' '}{t(`resume.experience.items.${exp.key}.title`)}</span>
                                </button>
                              )}
                            </>
                          );
                        })()}
                        {exp.link && linkText && (
                          <a 
                            href={exp.link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={getLinkStyles()}
                          >
                            {linkText}
                          </a>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Sidebar — education, highlights, CV (sticky on desktop) */}
              <aside className="flex flex-col gap-8 lg:sticky lg:top-32">
              <div className={getCardStyles()}>
                <div className={getSectionTitleStyles()}>
                  <FaGraduationCap className={getIconStyles()} />
                  <h3>{t('resume.education.title')}</h3>
                </div>
                <div className="relative">
                  {education.map((edu, index) => (
                    <div 
                      key={index} 
                      className="relative ps-8 sm:ps-10 lg:ps-12"
                      onMouseEnter={() => setHoveredIndex(index + experiences.length)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      {/* Timeline dot */}
                      <div 
                        className={`absolute start-0 top-2 sm:top-3 w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 rounded-full transition-all duration-300 ${
                          theme === 'modern' 
                            ? (hoveredIndex === index + experiences.length ? 'bg-emerald-500 scale-125' : 'bg-emerald-200')
                            : (hoveredIndex === index + experiences.length ? 'bg-purple-400 scale-125' : 'bg-black')
                        }`}
                      ></div>
                      <div className={getItemCardStyles(index, true)}>
                        <h4 className={getItemTitleStyles()}>{t(`resume.education.items.${edu.key}.degree`)}</h4>
                        <p className={getItemCompanyStyles()}>{t(`resume.education.items.${edu.key}.school`)}</p>
                        <p className={getItemPeriodStyles()}>{t(`resume.education.items.${edu.key}.period`)}</p>
                        {(() => {
                          const desc = t(`resume.education.items.${edu.key}.description`);
                          const itemKey = `edu-${index}`;
                          const isExpanded = !!expanded[itemKey];
                          const needsToggle = desc.length > DESC_LIMIT;
                          return (
                            <>
                              <p className={getItemDescriptionStyles()}>
                                {needsToggle && !isExpanded ? truncate(desc) : desc}
                              </p>
                              {needsToggle && (
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(itemKey)}
                                  className={theme === 'modern' ? 'sf-readmore' : 'nb-readmore'}
                                  aria-expanded={isExpanded}
                                >
                                  {isExpanded ? (t('resume.readLess', { defaultValue: 'Read less' })) : (t('resume.readMore', { defaultValue: 'Read more' }))}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className={getCardStyles()}>
                <div className={getSectionTitleStyles()}>
                  <FaStar className={getIconStyles()} />
                  <h3>{t('about.card.uniqueTitle')}</h3>
                </div>
                <ul className={theme === 'modern' ? 'rx-highlights rx-highlights--modern' : 'rx-highlights rx-highlights--classic'}>
                  {highlights.map((item, i) => (
                    <li key={i}>
                      <span className="rx-highlights__num">{String(i + 1).padStart(2, '0')}</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="/Moti_Rebuma-Resume.pdf"
                  download
                  className={theme === 'modern' ? 'rx-cv rx-cv--modern' : 'rx-cv rx-cv--classic'}
                >
                  <FaDownload aria-hidden="true" />
                  <span>{t('about.downloadCv')}</span>
                </a>
              </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Resume; 